import React, { useRef, useState } from "react";
import { FaTrash, FaUpload } from "react-icons/fa";
import { toast } from "react-toastify";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import { uploadCmsPageImage } from "../../services/cmsUpload.service";
import { DEFAULT_ABOUT_CMS_CONFIG } from "../../utils/aboutPageCms";

function Field({ label, children, hint }) {
  return (
    <div className="cms-contact-editor__field">
      <label className="cms-contact-editor__label">{label}</label>
      {children}
      {hint ? <p className="cms-contact-editor__hint">{hint}</p> : null}
    </div>
  );
}

function updateWhoCard(list, index, key, value) {
  return list.map((row, i) => (i === index ? { ...row, [key]: value } : row));
}

const ALLOWED_EXT = new Set(["jpg", "jpeg", "png", "webp"]);
const MAX_BYTES = 2 * 1024 * 1024;

export default function AboutCmsEditor({ config, onChange, readOnly = false }) {
  const data = config || DEFAULT_ABOUT_CMS_CONFIG;
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const patch = (partial) => onChange({ ...data, ...partial });

  const validateFile = (file) => {
    const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
    if (!ALLOWED_EXT.has(ext)) {
      toast.error("Only JPG, JPEG, PNG and WEBP images are allowed.");
      return false;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Background image must be 2 MB or smaller.");
      return false;
    }
    return true;
  };

  const handleBackgroundUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || readOnly || !validateFile(file)) return;

    setUploading(true);
    try {
      const imagePath = await uploadCmsPageImage(file, { fileType: "about_hero_background" });
      patch({ heroBackgroundUrl: imagePath });
      toast.success("Background image uploaded. Click Save to publish.");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Background image upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const clearBackground = () => {
    if (readOnly) return;
    patch({ heroBackgroundUrl: DEFAULT_ABOUT_CMS_CONFIG.heroBackgroundUrl });
  };

  const isCustomBackground =
    Boolean(data.heroBackgroundUrl) &&
    data.heroBackgroundUrl !== DEFAULT_ABOUT_CMS_CONFIG.heroBackgroundUrl;

  const previewUrl = data.heroBackgroundUrl ? resolvePublicFileUrl(data.heroBackgroundUrl) : "";

  return (
    <div className="cms-contact-editor">
      <p className="cms-contact-editor__intro">
        Update <strong>About Trocanet</strong>, vision/mission cards, and the hero background image
        for <strong>/about-us</strong>.
      </p>

      <section className="cms-contact-editor__section">
        <h3 className="cms-contact-editor__section-title">Hero background image</h3>
        <Field
          label="Background image"
          hint="Replaces the handshake visual on the About Us hero. JPG, PNG, or WEBP up to 2 MB."
        >
          <div className="cms-contact-editor__upload-row">
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
              hidden
              disabled={readOnly || uploading}
              onChange={handleBackgroundUpload}
            />
            <button
              type="button"
              className="cms-contact-editor__add-btn"
              disabled={readOnly || uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <FaUpload aria-hidden />
              {uploading ? "Uploading…" : "Upload image"}
            </button>
            {isCustomBackground ? (
              <button
                type="button"
                className="cms-contact-editor__delete-btn"
                disabled={readOnly || uploading}
                onClick={clearBackground}
              >
                <FaTrash aria-hidden />
                Reset default
              </button>
            ) : null}
          </div>
        </Field>
        {previewUrl ? (
          <div className="cms-contact-editor__preview">
            <img src={previewUrl} alt="About hero background preview" />
          </div>
        ) : (
          <p className="cms-contact-editor__empty">No background image set.</p>
        )}
      </section>

      <section className="cms-contact-editor__section">
        <h3 className="cms-contact-editor__section-title">About Trocanet</h3>
        <Field label="Description">
          <textarea
            rows={5}
            className="cms-contact-editor__textarea"
            value={data.whoParagraph1 || ""}
            disabled={readOnly}
            onChange={(e) => patch({ whoParagraph1: e.target.value, whoParagraph2: "" })}
          />
        </Field>
      </section>

      <section className="cms-contact-editor__section">
        <h3 className="cms-contact-editor__section-title">
          Vision, Mission, Values &amp; Commitment
        </h3>
        <div className="cms-contact-editor__subgrid">
          {(data.whoCards || []).map((card, index) => (
            <div key={card.key || `who-${index}`} className="cms-contact-editor__card">
              <Field label="Title">
                <input
                  type="text"
                  className="cms-contact-editor__input"
                  value={card.title || ""}
                  disabled={readOnly}
                  onChange={(e) =>
                    patch({ whoCards: updateWhoCard(data.whoCards, index, "title", e.target.value) })
                  }
                />
              </Field>
              <Field label="Description">
                <textarea
                  rows={3}
                  className="cms-contact-editor__textarea"
                  value={card.desc || ""}
                  disabled={readOnly}
                  onChange={(e) =>
                    patch({ whoCards: updateWhoCard(data.whoCards, index, "desc", e.target.value) })
                  }
                />
              </Field>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
